import _ from "lodash";
import { createRef } from "react";

// Interface for adding a field to a form
interface AddFieldParams {
  fieldArrayName: string;
  formRef: any;
  FormGroupInitialValues: any;
  FormConfig: any;
  setFormGroupInitialValues: (values: any) => void;
  setFormConfig: (config: any) => void;
  FormInitialValueObject?: any; // Optional parameter for adding new form group
  FormConfigObject?: any; // Optional parameter for adding new form group
  index?: number; // Optional parameter to handle multiple form references
}

// Interface for closing/removing a field from a form
interface CloseFieldParams {
  deletionIdx: number;
  fieldArrayName: string;
  formRef: any;
  FormGroupInitialValues: any;
  FormConfig: any;
  setFormGroupInitialValues: (values: any) => void;
  setFormConfig: (config: any) => void;
  index?: number; // Optional parameter to handle multiple form references
}

// Interface for setting initial values for a form or multiple forms
interface SetValueParams {
  details: any;
  formGroupObject: any;
  setFormGroupInitialValues: (values: any) => void;
  setFormConfig: (values: any) => void;
  setFormRef?: (config: any) => void; // Optional parameter only for multiple forms
}

// Interface for adding a new form group for multiple forms
interface AddNewFormGroupParams {
  FormGroupInitialValues: any;
  formInitialValueObject: any;
  formRef: any;
  FormConfig: any;
  formGroupObject: any;
  setFormGroupInitialValues: (values: any) => void;
  setFormRef: (ref: any) => void;
  setFormConfig: (config: any) => void;
}

// Function to add a new field to a form

/**
 * Adds a new field to the specified field array within a form or a specific indexed form group.
 * If an index is provided, it targets a specific form group; otherwise, it operates on a single form.
 * After adding the field, it updates the form group's initial values and form configuration accordingly.
 *
 * @param {AddFieldParams} params - The parameters for adding a field, including field array name, form reference,
 * initial form group values, form configuration, value setter functions, and optional index.
 */

export const addField = ({
  fieldArrayName,
  formRef,
  FormGroupInitialValues,
  FormConfig,
  setFormGroupInitialValues,
  setFormConfig,
  index,
  FormInitialValueObject,
  FormConfigObject,
}: AddFieldParams) => {
  // Clone the initial form group values to avoid direct mutations
  let copiedInitialValues = _.cloneDeep(FormGroupInitialValues);

  // Determine the field array object to be added based on the presence of an index
  let fieldArrayObj =
    index !== undefined
      ? copiedInitialValues[index][fieldArrayName][0]
      : copiedInitialValues[fieldArrayName][0];

  // If the field array object is undefined (i.e., no existing entries), use the provided initial value object
  if (!fieldArrayObj && FormInitialValueObject) {
    fieldArrayObj = FormInitialValueObject;
  }

  // Set the default values for the new field array object
  Object.keys(fieldArrayObj).forEach((key) => {
    fieldArrayObj[key] = "";
  });

  // Clone the current form values and update them with the new field array object
  let form;
  if (index !== undefined) {
    // Multi-form
    form = _.cloneDeep(formRef[index].current);
    copiedInitialValues[index] = { ...form.values };
    copiedInitialValues[index][fieldArrayName].push(fieldArrayObj);
  } else {
    // Single form
    form = _.cloneDeep(formRef.current);
    copiedInitialValues = { ...form.values };
    copiedInitialValues[fieldArrayName].push(fieldArrayObj);
  }

  // Update the form group's initial values with the modified values
  setFormGroupInitialValues(copiedInitialValues);

  // Clone the form configuration and update it with the new field array controls
  const copiedFormConfig = _.cloneDeep(FormConfig);

  let controls =
    index !== undefined // Multi-form
      ? copiedFormConfig?.formGroup[index]?.controls
      : copiedFormConfig?.controls; // Single form

  // If controls is undefined and FormConfigObject is provided, use FormConfigObject
  if (
    (!controls[0]?.fieldArray?.fieldArrayControls ||
      controls[0]?.fieldArray?.fieldArrayControls?.length === 0) &&
    FormConfigObject
  ) {
    controls = FormConfigObject;
    if (index !== undefined) {
      copiedFormConfig.formGroup[index].controls = controls;
    } else {
      copiedFormConfig.controls = controls;
    }
    // Update the form configuration with the modified controls
    setFormConfig(copiedFormConfig);
    return;
  }

  // Find the appropriate control group within the form configuration by the "fieldArrayName"
  for (let obj of controls) {
    if (obj?.fieldArray?.fieldArrayConfig?.fieldArrayName === fieldArrayName) {
      // Copy the control group structure from the predefined form configuration
      let newFieldArrayControls = _.cloneDeep(
        obj?.fieldArray?.fieldArrayControls[0]
      );

      // Push the copied control group to the form configuration's controls
      obj?.fieldArray?.fieldArrayControls?.push(newFieldArrayControls);
      break;
    }
  }

  // Update the form configuration with the modified controls
  setFormConfig(copiedFormConfig);
};

/**
 * Closes a field within the specified field array of a form or a specific indexed form group.
 * If an index is provided, it targets a specific form group; otherwise, it operates on a single form.
 * After closing the field, it updates the form group's initial values and form configuration accordingly.
 *
 * @param {CloseFieldParams} params - The parameters for closing a field, including deletion index,
 * field array name, form reference, initial form group values, form configuration,
 * value setter functions, and optional index.
 */

export const closeField = ({
  deletionIdx,
  fieldArrayName,
  formRef,
  FormGroupInitialValues,
  FormConfig,
  setFormGroupInitialValues,
  setFormConfig,
  index,
}: CloseFieldParams) => {
  // Clone the initial form group values to avoid direct mutations
  let copiedInitialValues = _.cloneDeep(FormGroupInitialValues);

  // Determine if the operation is on a multi-form or a single form
  if (index !== undefined) {
    // Multi-form: Clone the current form values and remove the field
    const form = _.cloneDeep(formRef[index].current);
    copiedInitialValues[index] = { ...form.values };
    copiedInitialValues[index][fieldArrayName].splice(deletionIdx, 1);
  } else {
    // Single form: Clone the current form values and remove the field
    const form = _.cloneDeep(formRef.current);
    copiedInitialValues = { ...form.values };
    copiedInitialValues[fieldArrayName].splice(deletionIdx, 1);
  }

  // Update the form group's initial values with the modified values
  setFormGroupInitialValues(copiedInitialValues);

  // Clone the form configuration and update it by removing the field array control
  const copiedFormConfig = _.cloneDeep(FormConfig);

  // Find the appropriate control group within the form configuration by the "fieldArrayName"
  const controls =
    index !== undefined
      ? copiedFormConfig?.formGroup[index]?.controls
      : copiedFormConfig?.controls;

  for (let obj of controls) {
    if (obj?.fieldArray?.fieldArrayConfig?.fieldArrayName === fieldArrayName) {
      // Remove the field array control from the form configuration's controls
      obj?.fieldArray?.fieldArrayControls?.splice(deletionIdx, 1);
      break;
    }
  }

  // Update the form configuration with the modified controls
  setFormConfig(copiedFormConfig);
};

/**
 * Sets values for a form group based on the provided details and form group configuration.
 * It initializes the form group's initial values and form configuration.
 * If details are an array, it handles multiple form groups; otherwise, it handles a single form group.
 *
 * @param {SetValueParams} params - The parameters for setting values, including details,
 * form group configuration, value setter functions, and optional form reference setter.
 */

export const setValues = ({
  details,
  formGroupObject,
  setFormGroupInitialValues,
  setFormConfig,
  setFormRef,
}: SetValueParams) => {
  /**
   * Initializes the form group object with the provided details and field array name.
   *
   * @param {any} formGroupObject - The form group configuration object.
   * @param {any} details - The details to initialize the form group with.
   * @param {string} fieldArrayName - The name of the field array within the form group.
   * @returns {any} - The initialized form group object.
   */

  const initializeFormGroupObject = (
    formGroupObject: any,
    details: any,
    fieldArrayName: string
  ) => {
    const copiedFormGroupObject = _.cloneDeep(formGroupObject);

    for (let obj of copiedFormGroupObject?.controls ?? []) {
      // matching fieldArrayName
      if (
        obj?.fieldArray?.fieldArrayConfig?.fieldArrayName === fieldArrayName
      ) {
        if (details[fieldArrayName]?.length > 0) {
          // Copy the control group structure from the predefined form configuration
          let copiedFieldArrayControl = _.cloneDeep(
            obj.fieldArray.fieldArrayControls[0]
          );

          // clear the form configuration's controls
          obj.fieldArray.fieldArrayControls = [];

          // Push the copied control group to the form configuration's controls
          for (let j = 0; j < details[fieldArrayName].length; j++) {
            obj.fieldArray.fieldArrayControls.push(copiedFieldArrayControl);
          }
        }
        break;
      }
    }

    return copiedFormGroupObject;
  };

  if (Array.isArray(details)) {
    // Set initial values for multiple form groups
    const data = _.cloneDeep(details);
    setFormGroupInitialValues([...data]);

    // Initialize form configuration for multiple form groups
    let copiedFormRef: any = [];
    let copiedFormConfig: any = {};
    let formGroup = [];

    for (let i = 0; i < details.length; i++) {
      copiedFormRef.push(createRef());

      const fieldArrayName = formGroupObject?.controls.find(
        (obj: any) => obj?.fieldArray?.fieldArrayConfig?.fieldArrayName
      )?.fieldArray?.fieldArrayConfig?.fieldArrayName;

      // Store the form configuration from details for specific "fieldArrayName"
      if (fieldArrayName) {
        formGroup.push(
          initializeFormGroupObject(formGroupObject, details[i], fieldArrayName)
        );
      }
    }

    copiedFormConfig["formGroup"] = formGroup;

    // Update the form configuration
    setFormConfig(copiedFormConfig);

    // Set form reference for multiple form groups
    if (setFormRef) {
      setFormRef(copiedFormRef);
    }
  } else {
    // Set initial values for a single form group
    let data = { ...details };
    setFormGroupInitialValues(data);

    // find fieldArrayName
    const fieldArrayName = formGroupObject?.controls.find(
      (obj: any) => obj?.fieldArray?.fieldArrayConfig?.fieldArrayName
    )?.fieldArray?.fieldArrayConfig?.fieldArrayName;

    // Update form configuration from details for a single form group
    if (fieldArrayName) {
      const updatedFormGroupObject = initializeFormGroupObject(
        formGroupObject,
        details,
        fieldArrayName
      );
      setFormConfig(updatedFormGroupObject);
    }
  }
};

/**
 * Adds a new form group to the existing form setup by updating initial values, form references, and form configuration.
 *
 * @param {AddNewFormGroupParams} params - The parameters for adding a new form group, including initial values,
 * form initial value object, form references, form configuration, form group object, and setter functions.
 */

export const addNewFormGroup = ({
  FormGroupInitialValues,
  formInitialValueObject,
  formRef,
  FormConfig,
  formGroupObject,
  setFormGroupInitialValues,
  setFormRef,
  setFormConfig,
}: AddNewFormGroupParams) => {
  // Clone the initial form group values to avoid direct mutations
  let copiedFormGroupInitialValues = _.cloneDeep(FormGroupInitialValues);

  // Update initial values for each form reference
  formRef.forEach((ref: any, index: number) => {
    const form: any = _.cloneDeep(ref.current);
    copiedFormGroupInitialValues[index] = {
      ...form.values,
    };
  });

  // Add the new form initial value object to the cloned initial values
  copiedFormGroupInitialValues.push(formInitialValueObject);
  setFormGroupInitialValues(copiedFormGroupInitialValues);

  // Clone the form references and add a new reference
  const copiedFormRef = _.cloneDeep(formRef);
  copiedFormRef.push(createRef());
  setFormRef(copiedFormRef);

  // Clone the form configuration and add the new form group object
  const copiedFormConfig = _.cloneDeep(FormConfig);
  copiedFormConfig.formGroup.push(formGroupObject);
  setFormConfig(copiedFormConfig);
};
